/**
 * usePreview — TanStack Query hook over
 * `POST /organizations/{slug}/reservations/preview` (SCRUM-337).
 *
 * Drives the live-quote panel on the create-reservation form. A stateless
 * quote is a POST-with-a-body, but it is a READ (idempotent, no side
 * effects) — so it is modelled as a `useQuery` keyed by its request shape,
 * NOT a mutation. This lets the panel dedupe / cache identical prospective
 * stays and re-quote automatically when the form input changes.
 *
 * Status union: `loading | ready | forbidden | invalidStay | error`.
 *   - `ready`       — 200 → double-unwrapped `ChargesBreakdownResponseDto`.
 *   - `loading`     — pending OR the enabled-gate is closed (no request).
 *   - `forbidden`   — 403 → the caller lacks `booking.reservations.read`.
 *   - `invalidStay` — 400 → a DISTINCT branch (checkOut<=checkIn, stay too
 *                     long, etc.). The form renders an inline invalid-stay
 *                     hint, NOT the generic error affordance.
 *   - `error`       — 404 / 422 / 5xx → generic error branch.
 *
 * Enabled gate (no request fired unless ALL hold):
 *   - org slug is resolved (RLS-scoped, CLAUDE.md non-negotiable §1),
 *   - `propertyId` non-empty,
 *   - `checkInDate` non-empty,
 *   - `checkOutDate` non-empty,
 *   - `guestCount >= 1`,
 *   - `checkOutDate > checkInDate` (ISO `YYYY-MM-DD` string compare — a
 *     lexicographic compare is date-correct for zero-padded ISO dates).
 *
 * usePreview is a PLAIN keyed query — it does NOT debounce internally. The
 * form (step 7) debounces the `PreviewInput` it feeds in; keeping the hook
 * debounce-free keeps it a pure, testable read.
 *
 * Sentinels: the queryFn returns `FORBIDDEN` on 403 and `INVALID_STAY` on
 * 400 (module-level Symbols); everything else non-ok / undefined-body
 * throws → `error`.
 *
 * MONEY: the breakdown is passed through untouched — no arithmetic here
 * (CLAUDE.md rule 6). The `toQuoteView` mapper + `formatCents` are the only
 * boundary the form uses.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { bookingQueryKeys } from './queryKeys.js';
import type { ChargesBreakdownResponseDto } from './types.js';

/**
 * 403 sentinel — the caller lost `booking.reservations.read` since
 * `/auth/me` cached. The form renders a permission-denied hint (no Retry).
 */
const FORBIDDEN = Symbol('reservation-preview:forbidden');

/**
 * 400 sentinel — the prospective stay is structurally invalid
 * (checkOut<=checkIn, stay exceeds the max nights, etc.). A DISTINCT,
 * non-error state: the form renders an inline invalid-stay hint.
 */
const INVALID_STAY = Symbol('reservation-preview:invalidStay');

/** The full preview input the form feeds in (all axes that alter the quote). */
export interface PreviewInput {
  readonly propertyId: string;
  readonly checkInDate: string;
  readonly checkOutDate: string;
  readonly guestCount: number;
  /** Children subset of `guestCount` (>= 0). Defaults to 0 on the wire. */
  readonly childCount?: number;
  /** Pets (>= 0). Defaults to 0 on the wire. */
  readonly petCount?: number;
  /** Vehicles (>= 0). Defaults to 0 on the wire. */
  readonly vehicleCount?: number;
  /** Opaque reservation-type discriminator, or null. */
  readonly reservationType: string | null;
  /** Opaque booking-channel discriminator, or null. */
  readonly bookingChannel: string | null;
}

export type UsePreviewResult =
  | { readonly status: 'loading'; readonly data?: undefined }
  | { readonly status: 'ready'; readonly data: ChargesBreakdownResponseDto }
  | { readonly status: 'forbidden'; readonly data?: undefined }
  | { readonly status: 'invalidStay'; readonly data?: undefined }
  | { readonly status: 'error'; readonly data?: undefined };

/**
 * The identifying subset the preview cache is keyed by (the full request
 * shape). Optional axes are normalised to their wire defaults so a
 * `childCount: undefined` and an explicit `childCount: 0` key identically.
 */
function toKeyInput(input: PreviewInput): {
  readonly propertyId: string;
  readonly checkInDate: string;
  readonly checkOutDate: string;
  readonly guestCount: number;
  readonly childCount: number;
  readonly petCount: number;
  readonly vehicleCount: number;
  readonly reservationType: string | null;
  readonly bookingChannel: string | null;
} {
  return {
    propertyId: input.propertyId,
    checkInDate: input.checkInDate,
    checkOutDate: input.checkOutDate,
    guestCount: input.guestCount,
    childCount: input.childCount ?? 0,
    petCount: input.petCount ?? 0,
    vehicleCount: input.vehicleCount ?? 0,
    reservationType: input.reservationType,
    bookingChannel: input.bookingChannel,
  };
}

export function usePreview(input: PreviewInput): UsePreviewResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const inputValid =
    input.propertyId.length > 0 &&
    input.checkInDate.length > 0 &&
    input.checkOutDate.length > 0 &&
    input.guestCount >= 1 &&
    // ISO `YYYY-MM-DD` is zero-padded → lexicographic compare == date compare.
    input.checkOutDate > input.checkInDate;

  const keyInput = toKeyInput(input);

  const query = useQuery({
    queryKey: [...bookingQueryKeys.preview(keyInput), orgSlug] as const,
    enabled: orgSlug !== null && inputValid,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.POST(
        '/organizations/{slug}/reservations/preview',
        {
          params: { path: { slug: orgSlug as string } },
          body: {
            propertyId: input.propertyId,
            checkInDate: input.checkInDate,
            checkOutDate: input.checkOutDate,
            guestCount: input.guestCount,
            childCount: input.childCount ?? 0,
            petCount: input.petCount ?? 0,
            vehicleCount: input.vehicleCount ?? 0,
            reservationType: input.reservationType,
            bookingChannel: input.bookingChannel,
          },
        },
      );
      if (result.response.status === 400) {
        return INVALID_STAY;
      }
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `POST /organizations/${orgSlug}/reservations/preview failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  if (orgSlug === null || !inputValid || query.isPending) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden' };
  }
  if (query.data === INVALID_STAY) {
    return { status: 'invalidStay' };
  }

  return { status: 'ready', data: query.data.data };
}
